import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useGetCartSummaryQuery } from '@/entities/cart'
import { isKnownRole, useGetSessionQuery, useListLegalEntitiesQuery } from '@/entities/session'
import { LogoutSplash, useLogout } from '@/features/auth-logout'
import { LocaleSwitch, useLocaleMenuGroup } from '@/features/locale-switch'
import { ThemeSwitch, useThemeMenuGroup } from '@/features/theme-switch'
import { usePendingLocale } from '@/shared/i18n'
import {
  actingModeOf,
  actingSearch,
  carriedModifier,
  readActingModifier,
  showToast,
  useAccess,
  useMediaQuery,
  withReturnTo,
  type AccessPair,
} from '@/shared/lib'
import { CounterBadge } from '@/shared/ui/CounterBadge'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/IconButton'
import { SearchAutocomplete } from '@/shared/ui/SearchAutocomplete'
import { Skeleton } from '@/shared/ui/Skeleton'
import { BrandMark } from '@/shared/ui/BrandMark'
import { AccountMenu } from '@/widgets/account-menu'
import {
  AppNav,
  AppShell,
  AppTopBar,
  ConnectionIndicator,
  NotificationBell,
  type AppNavItem,
} from '@/widgets/app-shell'
import { ProcessMonitor } from '@/widgets/process-monitor'
import { CABINET_ROUTES, isCurrentSection } from './routeRegistry'
import s from './AppLayout.module.css'

/**
 * Порог, с которого тема и язык стоят в шапке своими кнопками. Ниже ряд действий делят корзина
 * и аккаунт с названием компании (его видно при любой ширине), а тема и язык переезжают в меню
 * аккаунта теми же пунктами. Слот, а не CSS: пункт меню классом не спрятать — скрытый пункт
 * остался бы в обходе стрелками.
 */
const WIDE_QUERY = '(min-width: 768px)'

/** Все параметры модификатора: что из них перенести в пункт меню, решает пара цели. */
const ANY_MODIFIER: AccessPair = { accepts: ['account', 'unp', 'settlement', 'cart'] }

/** Заглушка страницы на время загрузки её чанка. Скелетон, а не спиннер на весь экран. */
function PageFallback() {
  const { t } = useTranslation()

  return (
    <div className={s.fallback} aria-busy="true" aria-label={t('layout.pageLoading')}>
      <Skeleton shape="line" size="lg" width="medium" />
      <Skeleton shape="block" size="lg" />
    </div>
  )
}

/**
 * Право-признак сотрудника импортёра. В `/auth/me` флага «сотрудник» нет, а роль сравнивать
 * запрещено (роль — справка). `manager.clients.view` — основа работы менеджера: клиенты,
 * закреплённые за ним в 1С (`docs/roles/01-model.md` §2.2); клиентскому набору прав оно не
 * выдаётся никогда. Кто работает за закреплённых клиентов, тот не действует от своего юрлица —
 * компании и её смены в шапке у него нет. `legalEntity: null` признаком не годится: у учётной
 * записи менеджера в легаси юрлицо бывает.
 */
const EMPLOYEE_PERMISSION = 'manager.clients.view'

/**
 * Аккаунт в шапке: виджет `AccountMenu` с сеансом, выходом и переходом на выбор компании.
 * Сеанс уже загружен воротами — здесь он читается из кеша. Второй запрос — список юрлиц:
 * признака смены в `/auth/me` нет (`BE-AUTH-10`), а пункт «Сменить компанию» есть лишь когда
 * бэк её разрешил (`summary.switchAvailable`). Пока список не пришёл или не загрузился, пункта
 * нет — меню не ждёт его.
 * У сотрудника компании нет вовсе: ни подписи, ни смены, и список юрлиц не запрашивается.
 * Пока бэк закрывает сеанс, поверх кабинета стоит заставка «Выходим…» (P-AUTH-02#U-09).
 */
function HeaderAccount({ compact }: { compact: boolean }) {
  const { t } = useTranslation()
  const { t: ta } = useTranslation('auth')
  const { can } = useAccess()
  const isEmployee = can(EMPLOYEE_PERMISSION)
  const { data: session } = useGetSessionQuery()
  const { data: legalEntities } = useListLegalEntitiesQuery(undefined, { skip: isEmployee })
  const { logout, isLoggingOut } = useLogout()
  const themeGroup = useThemeMenuGroup()
  const localeGroup = useLocaleMenuGroup()
  const pendingLocale = usePendingLocale()
  const navigate = useNavigate()
  const location = useLocation()

  /** Выход не удался (сеть, `5xx`): сеанс жив, пользователь в кабинете, тост с повтором (P-AUTH-02#U-10). */
  const signOut = async () => {
    if (await logout()) return
    showToast(ta('logout.error'), {
      tone: 'danger',
      announce: 'assertive',
      action: { label: t('action.retry'), onClick: () => void signOut() },
    })
  }

  // Без сеанса ворота в кабинет не пускают: пустой кеш здесь бывает только в такт выхода
  if (session === undefined) return null

  return (
    <>
      <AccountMenu
        user={session.user}
        company={isEmployee ? undefined : session.user.legalEntity}
        role={isKnownRole(session.role) ? session.role : undefined}
        onSwitchCompany={
          // Смену показывает бэк флагом summary.switchAvailable: одна привязка или вход без
          // юрлица из справочника закрывают её (activate ответил бы 403), сколько бы строк ни было
          !isEmployee && legalEntities?.summary.switchAvailable === true
            ? () => void navigate(withReturnTo('/select-company', location))
            : undefined
        }
        onLogout={() => void signOut()}
        preferences={compact ? [themeGroup, localeGroup] : undefined}
        // На узком экране язык выбирают в этом меню: ожидание словаря показывает его кнопка
        busy={compact && pendingLocale !== null}
      />

      {isLoggingOut ? <LogoutSplash /> : null}
    </>
  )
}

/**
 * Раскладка кабинета: липкая шапка с меню разделов во второй строке и `<Outlet />` роутера
 * в содержимом во всю ширину. Боковой колонки нет: разделов пять, они умещаются капсулами,
 * а главная и реестры получают всю ширину под данные. Один экземпляр на всё приложение,
 * вокруг всех маршрутов кроме дев-стенда.
 *
 * Живёт в `app`, а не в `widgets`: она собирает `widgets/app-shell` с `entities/cart`,
 * монитором процессов и роутером, а слайсы одного слоя друг друга не импортируют.
 *
 * Шапка: язык и тема (с 768px), корзина, аккаунт; строкой ниже — разделы, монитор процессов
 * и связь с 1С. За данными раскладка ходит ровно один раз — за счётчиком корзины и списком
 * юрлиц; сеанс уже в кеше, очередь 1С запрашивает монитор. Всё остальное приносят страницы.
 */
export function AppLayout() {
  const { t } = useTranslation()
  const { can } = useAccess()
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const isWide = useMediaQuery(WIDE_QUERY)

  const [params] = useSearchParams()

  /*
   * Корзина клиента — только при `cart.manage`: у менеджера её нет, и сводка не запрашивается
   * вовсе (иначе `403` на каждом экране). Ошибка и загрузка счётчика экран не ломают: капсулы
   * просто нет, а сама корзина откроется и покажет своё состояние.
   */
  const hasCart = can('cart.manage')
  const { data: cartSummary } = useGetCartSummaryQuery(undefined, { skip: !hasCart })
  const cartCount = cartSummary?.quantity ?? 0

  /* Колокольчик — только при праве на ленту: без него нет ни кнопки, ни запроса счётчика. */
  const hasNotifications = can('notification.view')

  /** Пустой запрос менять панель подсказок и футер не должен: подсказывать пока нечего. */
  const hasQuery = query.trim() !== ''

  /*
   * Меню — из реестра маршрутов: пункт виден, если раздел открывается без клиента (режим
   * `own` или `full`). Разделы, которым нужен клиент (`needs-client`: резервы и счета у
   * менеджера), в меню не попадают — они живут вкладками профиля клиента. Выбранный клиент
   * переносится в разделы, где у пользователя есть право «от имени» и ручка его принимает
   * (`docs/roles/03-frontend-architecture.md` §5): сейчас это каталог глазами клиента.
   */
  const currentModifier = readActingModifier(params, ANY_MODIFIER)
  /*
   * Текущий — самый длинный подходящий раздел: `/clients/debts` подсвечивает «Долги», а не
   * заодно и «Клиентов», чей адрес — его префикс. Профиль `/clients/:client/…` — «Клиенты».
   */
  const currentPath = CABINET_ROUTES.filter(
    (entry) => entry.nav !== undefined && isCurrentSection(entry.path, pathname),
  ).reduce<string | null>(
    (best, entry) => (best === null || entry.path.length > best.length ? entry.path : best),
    null,
  )
  const navItems: AppNavItem[] = CABINET_ROUTES.flatMap((entry) => {
    const { access, nav } = entry
    if (nav === undefined) return []
    if (access !== null) {
      const mode = actingModeOf(access, can, false)
      if (mode === 'none' || mode === 'needs-client') return []
    }

    const carried =
      access?.onBehalf !== undefined && can(access.onBehalf)
        ? carriedModifier(currentModifier, access)
        : undefined
    const to = `${entry.path}${actingSearch(carried)}`

    return [
      {
        id: entry.id,
        label: t(nav.labelKey),
        icon: <Icon name={nav.icon} size="sm" />,
        current: entry.path === currentPath,
        // Переход делает ссылка роутера: каркас о роутере не знает — он отдаёт слот
        render: (props) => <Link {...props} to={to} />,
      },
    ]
  })

  const header = (
    <AppTopBar
      logo={<BrandMark size="md" />}
      logoLabel={t('app.name')}
      logoLabelHidden
      renderLogo={({ className, children }) => (
        <Link className={className} to="/">
          {children}
        </Link>
      )}
      nav={<AppNav label={t('layout.navLabel')} items={navItems} trailing={<ProcessMonitor />} />}
      search={
        <SearchAutocomplete
          size="md"
          label={t('layout.searchLabel')}
          placeholder={t('layout.searchPlaceholder')}
          clearLabel={t('layout.searchClear')}
          value={query}
          onChange={setQuery}
          /*
           * Подсказок нет: лёгкой ручки-справочника по номенклатуре
           * (`/catalog/suggest`) в контракте бэка ещё нет, а тянуть страницу товаров
           * ради выпадашки запрещено правилом 11. Enter уводит в каталог с ?search=,
           * фильтрацию делает он же — на сервере.
           */
          empty={hasQuery ? t('layout.searchNoSuggest') : t('layout.searchHint')}
          submitLabel={hasQuery ? t('layout.searchSubmit') : undefined}
          onSubmit={(value) => {
            const search = value.trim()
            if (search === '') return
            void navigate(`/catalog?search=${encodeURIComponent(search)}`)
          }}
        />
      }
      actions={
        <>
          <ConnectionIndicator />

          {hasNotifications ? (
            <NotificationBell
              renderViewAllLink={({ className, children, onClick }) => (
                <Link className={className} to="/notifications" onClick={onClick}>
                  {children}
                </Link>
              )}
            />
          ) : null}

          {isWide ? (
            <>
              <LocaleSwitch />
              <ThemeSwitch />
            </>
          ) : null}

          {hasCart ? (
            <IconButton
              icon={<Icon name="cart" size="md" />}
              label={cartCount > 0 ? t('layout.cartCount', { count: cartCount }) : t('layout.cart')}
              variant="secondary"
              size="md"
              badge={
                cartCount > 0 ? (
                  <CounterBadge value={cartCount} tone="danger" size="xs" decorative />
                ) : undefined
              }
              onClick={() => void navigate('/cart')}
            />
          ) : null}

          <HeaderAccount compact={!isWide} />
        </>
      }
    />
  )

  return (
    <AppShell
      skipToContentLabel={t('layout.skipToContent')}
      scrollToTopLabel={t('layout.scrollToTop')}
      header={header}
    >
      {/* Граница ожидания внутри каркаса: при переходе меняется только содержимое */}
      <Suspense fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}
